import { create } from "zustand"
import type { client, ClientOutput } from "./ts-rest"
import type { ClientInferResponseBody } from "@ts-rest/core"
import type { contractRouter } from '@simulead/api/contract'

type AuthData = ClientInferResponseBody<typeof contractRouter.getAuthData, 200>
interface BearState {
  authProfile: AuthData["authProfile"] | undefined
  user: AuthData["authedUsers"][0] | undefined
  showLoaderBar: boolean
  commandMenuOpen: boolean
  // Tracks whether the onboarding modal is currently rendered-open so the
  // privacy modal can avoid stacking on top of it. Starts true so the privacy
  // modal stays hidden until onboarding has had a chance to evaluate.
  isOnboardingModalOpen: boolean
  // ENG-1214: which camera the user picked in the pre-call preview.
  // Ephemeral — no need to persist across sessions; users with one
  // camera never see the selector, multi-camera users re-pick each
  // time (and the browser's permission prompt's own picker covers the
  // very-first-time case). Read by VapiCall/LiveAPICall when opening
  // their camera stream so the call uses whatever device the user
  // confirmed in the preview tile.
  selectedCameraDeviceId: string | undefined
  // Lifted out of AppSidebar so it survives the brief LoggedInView unmount
  // during emulation switches (when `user` is cleared and re-derived).
  userPopoverOpen: boolean
  // Moves toasts off the bottom-centre of the viewport while a surface owns
  // that space. Set by the presentation run, where the self-view and the body
  // language activation sweep sit at the bottom of the screen and a toast
  // lands squarely over the presenter's face.
  avoidBottomCenterToasts: boolean
  // Home → Personal Scenario Creator handoff of the user's real-conversation
  // description. Held in memory only (never a search param, never persisted)
  // so the prose stays out of URLs, history and logs; the editor consumes and
  // clears it on arrival.
  pendingPersonalScenarioSituation: string | undefined
  // emulateUserId: number | undefined
  setStore: (data: Partial<Omit<BearState, "set">>) => void
}

export const useTempStore = create<BearState>()((set) => ({
  authProfile: undefined,
  user: undefined,
  showLoaderBar: true,
  commandMenuOpen: false,
  isOnboardingModalOpen: true,
  selectedCameraDeviceId: undefined,
  userPopoverOpen: false,
  avoidBottomCenterToasts: false,
  pendingPersonalScenarioSituation: undefined,
  // emulateUserId: undefined,
  setStore: set,
}))

// Home → Personal Scenario Creator: parks the situation in memory and returns
// the navigation target, which carries only the prose-free ?autostart flag.
export function handOffPersonalScenarioSituation({
  situation,
}: {
  situation: string
}) {
  useTempStore.getState().setStore({
    pendingPersonalScenarioSituation: situation,
  })
  return {
    to: "/scenario-creator" as const,
    search: { autostart: true as const },
  }
}
